import { useCallback, useEffect, useState } from "react";
import { playNotificationSound, vibrateNotification, getStoredSoundPref, setStoredSoundPref } from "@/lib/notificationSound";

const STORAGE_KEY = "talabya_driver_alerts_v1";

/**
 * Driver alerts: beep + vibrate on new incoming order.
 * Uses the shared notificationSound library so the driver's chosen tone matches
 * their profile preference. Toggle persisted in localStorage.
 */
export default function useDriverAlerts() {
  const [enabled, setEnabled] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) !== "0"; } catch { return true; }
  });
  const [soundId, setSoundId] = useState(() => getStoredSoundPref());

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, enabled ? "1" : "0");
  }, [enabled]);

  // Keep local state in sync if user changes it elsewhere (e.g., Profile page).
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "talabya_notif_sound_v1") setSoundId(e.newValue || "classic");
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const alertNewOrder = useCallback(() => {
    if (!enabled) return;
    playNotificationSound(soundId);
    vibrateNotification([300, 120, 300, 120, 300]);
  }, [enabled, soundId]);

  const alertSoft = useCallback(() => {
    if (!enabled) return;
    playNotificationSound(soundId === "silent" ? "silent" : "whistle");
    vibrateNotification([100]);
  }, [enabled, soundId]);

  const toggle = useCallback(() => setEnabled((v) => !v), []);
  const testAlert = useCallback(() => alertNewOrder(), [alertNewOrder]);
  const changeSound = useCallback((id) => {
    setSoundId(id);
    setStoredSoundPref(id);
  }, []);

  return { enabled, toggle, testAlert, alertNewOrder, alertSoft, soundId, changeSound };
}
